// @react
import React, { ReactElement, useRef } from 'react'
// @types
import { WorkFormValues } from 'modules/common/forms/types/work'
import {
  GetConstantsQueryResult,
  GetUserQueryResult,
} from 'lib/graphql/graphqlTypes'
// @libraries
import { Control, Controller, FieldErrors } from 'react-hook-form'
// @components
import Grid from '@bit/atd.web.grid'
import InputText from '@bit/atd.web.forms.input-text'
import Select from '@bit/atd.web.select'
// @module components
import PublicProfileBanner from 'modules/common/forms/banners/PublicProfile'
import strings from 'lib/constants/strings'

type PropsType = {
  userData?: GetUserQueryResult['data']
  constantData?: GetConstantsQueryResult['data']
  control: Control<WorkFormValues>
  errors: FieldErrors
  editing?: boolean
}

const WorkPublicFields = ({
  userData,
  constantData,
  control,
  errors,
  editing,
}: PropsType): ReactElement => {
  /****************************************
   * REFS
   *****************************************/

  const roleRef = useRef<HTMLInputElement | null>(null)
  const industryRef = useRef<HTMLInputElement | null>(null)

  /****************************************
   * PROPS
   *****************************************/

  const industryOptions =
    constantData?.constants.industryCodes.map((item: any) => {
      return {
        value: item.key,
        label: item.value,
      }
    }) || []

  const roleOptions =
    constantData?.constants.jobRoleCodes.map((item: any) => {
      return {
        value: item.key,
        label: item.value,
      }
    }) || []

  /****************************************
   * RENDER
   *****************************************/

  return (
    <>
      {/* Row 1 */}
      <Grid xs={12} item>
        <PublicProfileBanner userData={userData} />
      </Grid>

      {/* Industry / Role */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="industry"
          onFocus={() => industryRef?.current?.focus()}
          defaultValue={''}
          render={({ onChange, onBlur, value, name }) => (
            <Select
              // defaultValue={value}
              native
              name={name}
              readOnly={!editing}
              label="Industry"
              placeholder={strings.PROFILE.ACCESS.INDUSTRY}
              required
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
              inputRef={industryRef}
              options={industryOptions}
              error={!!errors?.industry}
              errorText={errors?.industry?.message}
            />
          )}
        />
      </Grid>
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="role"
          onFocus={() => roleRef?.current?.focus()}
          defaultValue={''}
          render={({ onChange, onBlur, value, name }) => (
            <Select
              //defaultValue={value}
              native
              name={name}
              readOnly={!editing}
              label="Role"
              placeholder={strings.PROFILE.ACCESS.ROLE}
              required
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
              inputRef={roleRef}
              options={roleOptions}
              error={!!errors?.role}
              errorText={errors?.role?.message}
            />
          )}
        />
      </Grid>

      {/* Job Title / Company Name */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="jobTitle"
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              name={name}
              readOnly={!editing}
              label="Job Title"
              placeholder={strings.PROFILE.ACCESS.JOB}
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
            />
          )}
        />
      </Grid>
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name="companyName"
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              name={name}
              readOnly={!editing}
              label="Company Name"
              placeholder={strings.PROFILE.ACCESS.C_NAME}
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
            />
          )}
        />
      </Grid>
    </>
  )
}

export default WorkPublicFields
